import type {DiffAnchor} from '@github-ui/diffs/types'
import {memo, type RefObject, useMemo} from 'react'

import {getDiffHunksData} from '../helpers/hunk-data-helpers'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import {useGridNavigation} from '../hooks/use-grid-navigation'
import type {DiffLine, ClientDiffLine, FileDiffReference} from '../types'
import {EMPTY_DIFF_LINE} from '../types'
import SplitDiffRow from './SplitDiffRow'
import {useDiffLinesActions, useGetFirstLineSelectionOnDrag} from '../stores/selectors'
import type {ViewerData} from '../conversation-types'

function groupDiffLines(lines: DiffLine[]): {
  leftLines: ClientDiffLine[]
  rightLines: ClientDiffLine[]
} {
  const leftLines: ClientDiffLine[] = []
  const rightLines: ClientDiffLine[] = []

  // Keep list of lines even, filling in blanks as needed
  const fillBlanks = () => {
    while (leftLines.length < rightLines.length) {
      leftLines.push(EMPTY_DIFF_LINE)
    }

    while (rightLines.length < leftLines.length) {
      rightLines.push(EMPTY_DIFF_LINE)
    }
  }

  for (const line of lines) {
    switch (line.type) {
      case 'ADDITION':
        rightLines.push(line)
        break
      case 'DELETION':
        leftLines.push(line)
        break
      case 'CONTEXT':
      case 'INJECTED_CONTEXT':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
        break
      case 'HUNK':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
    }
  }
  fillBlanks()

  return {leftLines, rightLines}
}

function useGroupedLines(diffLines: DiffLine[]) {
  const {leftLines, rightLines} = useMemo(() => groupDiffLines(diffLines), [diffLines])

  return {leftLines, rightLines}
}

const SplitDiffLines = memo(function SplitDiffLines({
  diffHasHiddenUnicodeCharacters,
  diffLines,
  fileAnchor,
  filePath,
  handleDiffSideCellSelectionBlocking,
  showHiddenUnicode,
  tableRef,
  copilotChatReference,
  viewerData,
}: {
  diffHasHiddenUnicodeCharacters: boolean
  diffLines: DiffLine[]
  fileAnchor: DiffAnchor
  fileLineCount: number
  filePath: string
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  showHiddenUnicode: boolean
  tableRef: RefObject<HTMLTableElement | null>
  copilotChatReference?: FileDiffReference
  viewerData: ViewerData
}) {
  const {leftLines, rightLines} = useGroupedLines(diffLines)
  const hunksData = useMemo(() => getDiffHunksData(leftLines), [leftLines])
  const {addLineToSelection, clearSelectedLines, selectMultipleLines} = useDiffLinesActions()
  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()

  useGridNavigation({
    addLineToSelection,
    clearSelectedLines,
    containerRef: tableRef,
    fileAnchor,
    getfirstLineSelectionOnDrag,
    isSplitDiff: true,
    leftLines,
    selectMultipleLines,
    rightLines,
    disabled: false,
  })

  return (
    <>
      {leftLines.map((_, index) => {
        const leftDiffLine = leftLines[index] as ClientDiffLine
        const rightDiffLine = rightLines[index] as ClientDiffLine
        const prevDiffLine = leftLines[index - 1]
        const nextDiffLine = isEmptyDiffLine(leftLines[index + 1]) ? rightLines[index + 1] : leftLines[index + 1]

        const key = `${
          isEmptyDiffLine(leftDiffLine) || !leftDiffLine ? `empty-left-diff-${index}` : leftDiffLine.blobLineNumber
        }-${
          isEmptyDiffLine(rightDiffLine) || !rightDiffLine ? `empty-right-diff-${index}` : rightDiffLine.blobLineNumber
        }`

        return (
          <SplitDiffRow
            copilotChatReference={copilotChatReference}
            diffHasHiddenUnicodeCharacters={diffHasHiddenUnicodeCharacters}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
            hunksData={hunksData}
            key={key}
            leftDiffLine={leftDiffLine}
            nextDiffLine={nextDiffLine}
            prevDiffLine={prevDiffLine}
            rightDiffLine={rightDiffLine}
            showHiddenUnicode={showHiddenUnicode}
            viewerData={viewerData}
          />
        )
      })}
    </>
  )
})

export default SplitDiffLines

try{ SplitDiffLines.displayName ||= 'SplitDiffLines' } catch {}